import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarClock, FileSignature, Loader2, Pencil, Plus, ReceiptText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { AmountInput } from '../components/ui/amount-input';
import RowActions from '../components/RowActions';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const STATUS = {
  pending: { label: 'status.pending', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  partial: { label: 'status.partial', cls: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
  paid: { label: 'status.paid', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
};
const INVOICE_STATUS = { draft: 'invoiceStatus.draft', sent: 'invoiceStatus.sent', paid: 'invoiceStatus.paid' };
const AKT_STATUS = { draft: 'aktStatus.draft', final: 'aktStatus.final' };
const fmtMoney = (v, cur) => `${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${cur}`;
const errorText = (err, fallback) => err?.response?.data?.detail || fallback;

export default function IncomeEstimates() {
  const { t } = useTranslation('incomeEstimates');
  const MONTHS = t('common:months', { returnObjects: true });
  const { user } = useAuth();
  const canWrite = !['accountant', 'employee', 'pending'].includes(user?.role);
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | estimate
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ year });
      if (month !== 'all') params.set('month', month);
      const res = await axios.get(`${API_URL}/api/income-estimates?${params}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(errorText(err, t('common:status.loadFailed')));
      setData({ items: [], totals: {} });
    }
  }, [year, month, t]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API_URL}/api/projects`, { withCredentials: true })
      .then((res) => setProjects(Array.isArray(res.data) ? res.data : (res.data?.items || [])))
      .catch(() => setProjects([]));
  }, []);

  const openNew = () => {
    const m = month === 'all' ? String(now.getMonth() + 1) : month;
    setForm({ project_id: '', month: m, year, amount: '', currency: 'USD', note: '', ranged: false, to_month: '12', to_year: year });
    setEditing({});
  };
  const openEdit = (est) => {
    setForm({ project_id: est.project_id, month: String(est.month), year: String(est.year), amount: String(est.amount), currency: est.currency, note: est.note || '' });
    setEditing(est);
  };

  const save = async () => {
    if (!form.project_id) { toast.error(t('selectProject')); return; }
    setSaving(true);
    try {
      const { ranged, to_month: toMonth, to_year: toYear, ...rest } = form;
      const body = { ...rest, month: parseInt(form.month, 10), year: parseInt(form.year, 10), amount: parseFloat(form.amount) || 0 };
      // a range creates the same amount for every month in it (months that already have one are skipped)
      if (!editing?.id && ranged) Object.assign(body, { to_month: parseInt(toMonth, 10), to_year: parseInt(toYear, 10) });
      const res = editing?.id
        ? await axios.put(`${API_URL}/api/income-estimates/${editing.id}`, body, { withCredentials: true })
        : await axios.post(`${API_URL}/api/income-estimates`, body, { withCredentials: true });
      toast.success(res.data?.message || t('common:status.saved'));
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common:status.saveFailed')));
    } finally { setSaving(false); }
  };

  const remove = async (est) => {
    if (!window.confirm(t('confirmDelete', { project: est.project_name, period: `${MONTHS[est.month - 1]} ${est.year}` }))) return;
    try {
      await axios.delete(`${API_URL}/api/income-estimates/${est.id}`, { withCredentials: true });
      toast.success(t('common:status.deleted'));
      load();
    } catch (err) { toast.error(errorText(err, t('common:status.deleteFailed'))); }
  };

  const invoiceLink = (est) => `/client-invoices?${new URLSearchParams({
    new: '1', project: est.project_id, month: est.month, year: est.year, amount: est.amount, currency: est.currency,
  })}`;
  // AKT page: a new AKT prefilled with this row, or the project's AKTs for that year
  const aktNewLink = (est) => `/akt?${new URLSearchParams({
    new: '1', project: est.project_id, month: est.month, year: est.year, amount: est.amount, currency: est.currency,
  })}`;
  const aktListLink = (est) => `/akt?${new URLSearchParams({ project: est.project_id, year: est.year })}`;
  const years = Array.from({ length: 4 }, (_, i) => String(now.getFullYear() - 2 + i));
  const items = data?.items || [];

  return (
    <div className="space-y-6" data-testid="income-estimates-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <CalendarClock className="h-7 w-7 text-blue-600" /> {t('title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('subtitle')}
          </p>
        </div>
        {canWrite && <Button onClick={openNew} className="gap-2" data-testid="new-estimate-btn"><Plus className="h-4 w-4" /> {t('common:actions.add')}</Button>}
      </div>

      <div className="flex flex-wrap gap-3">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-full sm:w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('wholeYear')}</SelectItem>
            {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-full sm:w-32"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {data && Object.keys(data.totals || {}).length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.combined && (() => {
            const pct = (tot) => (tot.expected ? Math.min(100, Math.round((tot.received / tot.expected) * 100)) : 0);
            const parts = Object.entries(data.totals).map(([cur, tot]) => fmtMoney(tot.expected, cur));
            return (
              <div className="sm:col-span-2 lg:col-span-3 rounded-xl border-2 border-blue-200 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-950/20 p-4" data-testid="estimate-total-combined">
                <p className="text-xs uppercase text-slate-500">{t('total.title')}</p>
                {/* the same total in both currencies at once */}
                <div className="grid gap-4 sm:grid-cols-2 mt-2">
                  {['UZS', 'USD'].map((cur) => {
                    const tot = data.combined[cur] || { expected: 0, received: 0 };
                    return (
                      <div key={cur} data-testid={`total-${cur}`}>
                        <p className="text-xl font-bold">{fmtMoney(tot.received, cur)} <span className="text-sm font-normal text-slate-500">/ {fmtMoney(tot.expected, cur)}</span></p>
                        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 mt-2 overflow-hidden">
                          <div className="h-full bg-emerald-500" style={{ width: `${pct(tot)}%` }} />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{t('progress', { pct: pct(tot), rest: fmtMoney(Math.max(0, tot.expected - tot.received), cur) })}</p>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-400 mt-2">
                  {t('total.breakdown', { list: parts.join(' + ') })}
                  {data.combined.usd_to_uzs ? ` · ${t('total.rate', { rate: fmtMoney(data.combined.usd_to_uzs, 'UZS') })}` : ''}
                  {data.combined.missing?.length > 0 && <span className="block text-amber-600 dark:text-amber-400">{t('total.missing', { list: data.combined.missing.join(', ') })}</span>}
                </p>
              </div>
            );
          })()}
          {Object.entries(data.totals).map(([cur, tot]) => {
            const pct = tot.expected ? Math.min(100, Math.round((tot.received / tot.expected) * 100)) : 0;
            return (
              <div key={cur} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4" data-testid={`estimate-total-${cur}`}>
                <p className="text-xs uppercase text-slate-500">{cur}</p>
                <p className="text-xl font-bold mt-1">{fmtMoney(tot.received, cur)} <span className="text-sm font-normal text-slate-500">/ {fmtMoney(tot.expected, cur)}</span></p>
                <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 mt-3 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-xs text-slate-500 mt-1">{t('progress', { pct, rest: fmtMoney(Math.max(0, tot.expected - tot.received), cur) })}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
        {data === null ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-slate-500">{t('empty')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase text-slate-500">
              <tr>
                {['table.project', 'table.period', 'table.expected', 'table.received', 'table.status', 'table.invoice', 'table.akt', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h ? t(h) : ''}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {items.map((est) => {
                const st = STATUS[est.status] || STATUS.pending;
                return (
                  <tr key={est.id} data-testid={`estimate-row-${est.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium whitespace-nowrap">{est.project_name || est.project_id}</div>
                      {est.note && <div className="text-xs text-slate-500">{est.note}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{MONTHS[est.month - 1]} {est.year}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold">{fmtMoney(est.amount, est.currency)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{fmtMoney(est.received, est.currency)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${st.cls}`} data-testid={`estimate-status-${est.id}`}>{t(st.label)}</span>
                      {est.overdue && <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-red-600"><AlertTriangle className="h-3 w-3" /> {t('overdue')}</span>}
                    </td>
                    <td className="px-4 py-3">
                      {(est.invoices || []).map((inv) => (
                        <div key={inv.id} className="text-xs whitespace-nowrap"><span className="font-mono">{inv.number}</span> <span className="text-slate-500">({INVOICE_STATUS[inv.status] ? t(INVOICE_STATUS[inv.status]) : inv.status})</span></div>
                      ))}
                      {canWrite && est.status !== 'paid' && !(est.invoices || []).length && (
                        <Link to={invoiceLink(est)} title={t('createInvoiceHint')} className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline whitespace-nowrap"><ReceiptText className="h-3.5 w-3.5" /> {t('createInvoice')}</Link>
                      )}
                    </td>
                    <td className="px-4 py-3" data-testid={`estimate-akt-${est.id}`}>
                      {(est.akts || []).map((a) => (
                        <div key={a.id} className="text-xs whitespace-nowrap">
                          <Link to={aktListLink(est)} title={t('openAktHint')} className="font-mono text-blue-600 hover:underline">{a.number}</Link>{' '}
                          <span className="text-slate-500">({AKT_STATUS[a.status] ? t(AKT_STATUS[a.status]) : a.status})</span>
                        </div>
                      ))}
                      {canWrite && est.akt_can_create && !(est.akts || []).length && (
                        <Link to={aktNewLink(est)} title={t('createAktHint')} data-testid={`estimate-create-akt-${est.id}`}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline whitespace-nowrap"><FileSignature className="h-3.5 w-3.5" /> {t('createAkt')}</Link>
                      )}
                      {!(est.akts || []).length && !(canWrite && est.akt_can_create) && (
                        <span className="text-xs text-slate-400" title={est.akt_required ? t('aktInactiveHint') : t('aktNotRequiredHint')}>—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {canWrite && (
                        <RowActions actions={[
                          { key: 'edit', label: t('common:actions.edit'), hint: t('editHint'), icon: Pencil, onClick: () => openEdit(est), testId: `estimate-edit-${est.id}` },
                          { key: 'delete', label: t('common:actions.delete'), hint: t('deleteHint'), icon: Trash2, className: 'text-red-500', onClick: () => remove(est), testId: `estimate-delete-${est.id}` },
                        ]} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v && !saving) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing?.id ? t('common:actions.edit') : t('title')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>{t('common:fields.project')}</Label>
              <Select value={form.project_id || undefined} onValueChange={(v) => setForm({ ...form, project_id: v })} disabled={!!editing?.id}>
                <SelectTrigger data-testid="estimate-project"><SelectValue placeholder={t('selectProject')} /></SelectTrigger>
                <SelectContent>
                  {/* Only active projects can get new expected income; an edited row keeps its own project */}
                  {projects.filter((p) => p.status === 'active' || p.project_id === form.project_id)
                    .map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!editing?.id && (
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={!!form.ranged} onCheckedChange={(v) => setForm({ ...form, ranged: !!v })} data-testid="estimate-ranged" />
                {t('range.toggle')}
              </label>
            )}
            {form.ranged && !editing?.id && <p className="text-xs text-slate-500 -mt-2">{t('range.hint')}</p>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{form.ranged && !editing?.id ? t('range.from') : t('common:fields.month')}</Label>
                <Select value={form.month} onValueChange={(v) => setForm({ ...form, month: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>{t('common:fields.year')}</Label>
                <Select value={form.year} onValueChange={(v) => setForm({ ...form, year: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {form.ranged && !editing?.id && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{t('range.to')}</Label>
                  <Select value={form.to_month} onValueChange={(v) => setForm({ ...form, to_month: v })}>
                    <SelectTrigger data-testid="estimate-to-month"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>{t('common:fields.year')}</Label>
                  <Select value={form.to_year} onValueChange={(v) => setForm({ ...form, to_year: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="grid grid-cols-[1fr_100px] gap-3">
              <div className="space-y-1.5"><Label>{t('common:fields.amount')}</Label>
                <AmountInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} placeholder="0" data-testid="estimate-amount" />
              </div>
              <div className="space-y-1.5"><Label>{t('common:fields.currency')}</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['USD', 'UZS', 'EUR'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>{t('common:fields.note')}</Label>
              <Textarea rows={2} value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>{t('common:actions.cancel')}</Button>
            <Button onClick={save} disabled={saving} data-testid="estimate-save">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />} {t('common:actions.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
